// ViewTaskPage.jsx
import React, { useState, useEffect } from "react";
import { useAuth } from "../hooks/AuthContext";
import { useGetTasks } from "../hooks/useTaskApi";
import { TaskList } from "../components/tasks/TaskList";
import Loader from "../components/ui/Loader";
import { RotateCcw } from "lucide-react";
import api from "../lib/api"; // Your API instance

export default function ViewTaskPage() {
  const { user } = useAuth();
  const [page, setPage] = useState(1);
  const limit = 10;

  // 🔹 Filter states
  const [searchInput, setSearchInput] = useState(""); // User input
  const [searchFilter, setSearchFilter] = useState(""); // Actual search sent to API
  const [statusFilter, setStatusFilter] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [projectFilter, setProjectFilter] = useState(""); // Project _id

  const [projects, setProjects] = useState([]); // Projects dropdown data

  // ✅ Fetch all projects for project filter dropdown
  useEffect(() => {
    let isMounted = true;
    const fetchProjects = async () => {
      try {
        const res = await api.get("/projects");
        if (isMounted && res.data?.data) setProjects(res.data.data);
      } catch (err) {
        console.error("Error fetching projects:", err);
      }
    };
    fetchProjects();
    return () => {
      isMounted = false;
    };
  }, []);

  // ✅ Debounce search - wait 500ms after user stops typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchFilter(searchInput);
      setPage(1); // Reset to page 1 when search changes
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // ✅ Pass filters to API - backend handles filtering now
  // Make sure projectFilter `_id` value is passed
  const { data: taskData, isLoading, isError, error } = useGetTasks(
    page,
    limit,
    user,
    searchFilter,
    statusFilter,
    fromDate,
    toDate,
    projectFilter // This should be a _id string or empty ""
  );

  const TaskData = taskData?.tasks || [];
  const totalPages = taskData?.pages || 1;

  // ✅ Reset filters handler
  const handleResetFilters = () => {
    setSearchInput("");
    setSearchFilter("");
    setStatusFilter("");
    setFromDate("");
    setToDate("");
    setProjectFilter("");
    setPage(1);
  };

  // ✅ Pagination handler
  const onPageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  if (isError) {
    return <p className="text-red-500 text-center">{error.message}</p>;
  }

  if (isLoading) {
    return <Loader />;
  }

  return (
    <div className="p-6">
      <h1 className="text-xl font-bold mb-4">View Tasks</h1>
      {/* 🔹 Filters - Single Row Responsive Layout */}
      <div className="border-b pb-4 mb-6">
        <div className="flex flex-wrap items-end gap-3">

          {/* Search with Debounce */}
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-gray-700">
              Search Task
            </label>
            <input
              type="text"
              placeholder={
                user?.role === "employee"
                  ? "Search by title or description..."
                  : "Search by title or assignee..."
              }
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="mt-1 border rounded-md px-3 py-2 text-sm w-52"
            />
          </div>

          {/* Status */}
          <div className="min-w-[150px]">
            <label className="block text-sm font-medium text-gray-700">
              Status
            </label>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              className="mt-1 border rounded-md px-3 py-2 text-sm w-full"
            >
              <option value="">All</option>
              <option value="pending">Pending</option>
              <option value="to-do">To Do</option>
              <option value="in-progress">In Progress</option>
              <option value="hold">Hold</option>
              <option value="completed">Completed</option>
            </select>
          </div>

          {/* Project Filter */}
          <div className="min-w-[200px]">
            <label className="block text-sm font-medium text-gray-700">
              Project
            </label>
            <select
              value={projectFilter}
              onChange={(e) => {
                setProjectFilter(e.target.value); // Always string value _id or ""
                setPage(1);
              }}
              className="mt-1 border rounded-md px-3 py-2 text-sm w-full"
            >
              <option value="">All Projects</option>
              {projects.map((project) => (
                <option key={project._id} value={project._id}>
                  {project.title}
                </option>
              ))}
            </select>
          </div>

          {/* From Date */}
          <div className="min-w-[140px]">
            <label className="block text-sm font-medium text-gray-700">
              From Date
            </label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => {
                setFromDate(e.target.value);
                setPage(1);
              }}
              className="mt-1 border rounded-md px-3 py-2 text-sm w-full"
            />
          </div>

          {/* To Date */}
          <div className="min-w-[140px]">
            <label className="block text-sm font-medium text-gray-700">
              To Date
            </label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => {
                setToDate(e.target.value);
                setPage(1);
              }}
              className="mt-1 border rounded-md px-3 py-2 text-sm w-full"
            />
          </div>

          {/* Reset Button */}
          <button
            type="button"
            onClick={handleResetFilters}
            className="bg-gray-200 flex items-center gap-2 hover:bg-gray-300 text-gray-700 px-3 py-2 rounded-md text-sm h-[38px] mb-[1px]"
          >
            <RotateCcw size={17} />
            <span className="text-xs">Reset</span>
          </button>
        </div>
      </div>

      {/* 🔹 Task List */}
      <TaskList
        tasks={TaskData}
        isHrView={user?.role !== "employee"}
        paginationData={{ page, pages: totalPages }}
        onPageChange={onPageChange}
      />
    </div>
  );
}
